import { isAxiosError } from 'axios'
import { useMemo, useState } from 'react'

import { Toasts } from '@fourthwall/components'
import { getDefaultErrorMessage } from '@fourthwall/utils/dist/queryClient'

import { useFetchShipStationStoresQuery } from '../queries/useFetchShipStationStoresQuery'
import { useShipStationStoresQuery } from '../queries/useShipStationStoresQuery'

type Values = {
  apiKey?: string
  apiSecret?: string
}

export const useShipStationStores = (
  status: 'CONNECTED' | 'NOT_CONNECTED' | 'NOT_CONFIGURED' | undefined,
) => {
  const [values, setValues] = useState<Values | undefined>({})
  const isUsingCustomValues = !!values?.apiKey?.length && !!values?.apiSecret?.length

  const handleError = (error: unknown) => {
    // NOTE: those errors are handled as banners
    if (
      isAxiosError(error) &&
      [
        'SHIPSTATION_INCORRECT_CONFIGURATION_ERROR',
        'SHIPSTATION_INCORRECT_CREDENTIALS_ERROR',
        'SHIPSTATION_PAYMENT_REQUIRED_ERROR',
        'SHIPSTATION_INCORRECT_SETTINGS_ERROR',
      ].includes(error.response?.data?.code)
    ) {
      return
    }

    // NOTE: default handler
    Toasts.notify(getDefaultErrorMessage(error), { type: 'error' })
  }

  const { fetchShipStationStoresQuery, invalidateFetchShipStationStoresQuery } =
    useFetchShipStationStoresQuery(
      [
        {
          apiKey: values?.apiKey!,
          apiSecret: values?.apiSecret!,
        },
      ],
      {
        enabled: isUsingCustomValues,
        retry: false,
        keepPreviousData: true,
        onError: handleError,
      },
    )

  const { shipStationStoresQuery, invalidateShipStationStoresQuery } = useShipStationStoresQuery(
    undefined,
    {
      enabled: status === 'CONNECTED' && !isUsingCustomValues,
      retry: false,
      onError: handleError,
    },
  )

  const stores = useMemo(() => {
    if (isUsingCustomValues) {
      return !values?.apiKey?.length || !values?.apiSecret?.length
        ? undefined
        : fetchShipStationStoresQuery.data?.stores
    }

    if (status === 'CONNECTED') {
      return shipStationStoresQuery.data?.stores || fetchShipStationStoresQuery.data?.stores
    }
  }, [
    isUsingCustomValues,
    status,
    values,
    shipStationStoresQuery.data?.stores,
    fetchShipStationStoresQuery.data?.stores,
  ])

  const isLoadingStores = useMemo(() => {
    if (isUsingCustomValues) {
      return fetchShipStationStoresQuery.isFetching
    }

    if (status === 'CONNECTED') {
      return shipStationStoresQuery.isFetching
    }

    return false
  }, [
    isUsingCustomValues,
    status,
    shipStationStoresQuery.isFetching,
    fetchShipStationStoresQuery.isFetching,
  ])

  const errorCode = useMemo(() => {
    if (isUsingCustomValues && isAxiosError(fetchShipStationStoresQuery.error)) {
      return fetchShipStationStoresQuery.error?.response?.data?.code || ''
    }

    if (status === 'CONNECTED' && isAxiosError(shipStationStoresQuery.error)) {
      return shipStationStoresQuery.error?.response?.data?.code || ''
    }

    return ''
  }, [isUsingCustomValues, status, shipStationStoresQuery.error, fetchShipStationStoresQuery.error])

  return {
    values,
    stores,
    isLoadingStores,
    errorCode,
    setValues,
    invalidate: async () => {
      await Promise.all([
        invalidateFetchShipStationStoresQuery(),
        invalidateShipStationStoresQuery(),
      ])
    },
  }
}
